import { A } from '@solidjs/router';
import { createEffect, onCleanup } from 'solid-js';
import PublicLayout from './PublicLayout';
import { t, locale } from '@/shared/i18n';
import { usePublicPath } from '@/shared/lib/usePublicPath';

export default function BillingTermsPage() {
  createEffect(() => {
    const lang = locale() === 'vi' ? 'vi' : 'en';
    const path = lang === 'vi' ? '/vi/billing-terms' : '/billing-terms';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: t('billingTerms.title'),
      description: t('seo.billingTerms.desc'),
      inLanguage: lang,
      url: `https://khoai.io${path}`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'khoai.io',
        url: 'https://khoai.io',
      },
    };

    let script = document.getElementById(
      'ld-json-billing-terms'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-billing-terms';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
  });

  onCleanup(() => {
    document.getElementById('ld-json-billing-terms')?.remove();
  });

  const link = 'text-blue-600 hover:underline';
  const sectionCls = 'mt-10';
  const h2Cls = 'text-xl font-bold text-text-primary';
  const pCls = 'mt-3 text-sm leading-relaxed text-text-secondary';
  const p2Cls = 'mt-2 text-sm leading-relaxed text-text-secondary';
  const ulCls = 'mt-3 list-disc space-y-1 pl-6 text-sm text-text-secondary';
  const ul2Cls = 'mt-2 list-disc space-y-1 pl-6 text-sm text-text-secondary';
  const pp = usePublicPath();

  return (
    <PublicLayout>
      <article class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <h1 class="text-3xl font-extrabold text-text-primary sm:text-4xl">
          {t('billingTerms.title')}
        </h1>
        <p class="mt-2 text-sm text-text-secondary">
          {t('billingTerms.effective')}
        </p>

        {/* 1. Plans and Pricing */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s1.title')}</h2>
          <p class={pCls}>
            {t('billingTerms.s1.p1')}{' '}
            <A href={pp('/pricing')} class={link}>
              {t('public.nav.pricing')}
            </A>
            .
          </p>
        </section>

        {/* 2. Billing Cycle */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s2.title')}</h2>
          <p class={pCls}>{t('billingTerms.s2.p1')}</p>
        </section>

        {/* 3. Currency */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s3.title')}</h2>
          <p class={pCls}>{t('billingTerms.s3.p1')}</p>
        </section>

        {/* 4. Payment Methods */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s4.title')}</h2>
          <p class={pCls}>{t('billingTerms.s4.p1')}</p>
          <ul class={ul2Cls}>
            <li>{t('billingTerms.s4.li1')}</li>
            <li>{t('billingTerms.s4.li2')}</li>
            <li>{t('billingTerms.s4.li3')}</li>
          </ul>
          <p class={p2Cls}>{t('billingTerms.s4.p2')}</p>
        </section>

        {/* 5. Add-Ons */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s5.title')}</h2>
          <p class={pCls}>{t('billingTerms.s5.p1')}</p>
          <p class={p2Cls}>{t('billingTerms.s5.p2')}</p>
        </section>

        {/* 6. Upgrades and Downgrades */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s6.title')}</h2>
          <ul class={ulCls}>
            <li>{t('billingTerms.s6.li1')}</li>
            <li>{t('billingTerms.s6.li2')}</li>
          </ul>
        </section>

        {/* 7. Free Plan and Free Trial */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s7.title')}</h2>
          <p class={pCls}>{t('billingTerms.s7.p1')}</p>
          <p class={p2Cls}>{t('billingTerms.s7.p2')}</p>
          <p class={p2Cls}>{t('billingTerms.s7.p3')}</p>
        </section>

        {/* 8. Auto-Renewal */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s8.title')}</h2>
          <p class={pCls}>{t('billingTerms.s8.p1')}</p>
        </section>

        {/* 9. Failed Payments */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s9.title')}</h2>
          <p class={pCls}>{t('billingTerms.s9.p1')}</p>
        </section>

        {/* 10. Refunds */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s10.title')}</h2>
          <ul class={ulCls}>
            <li>{t('billingTerms.s10.li1')}</li>
            <li>
              {t('billingTerms.s10.li2')}{' '}
              <a href="mailto:human.khoai@gmail.com" class={link}>
                human.khoai@gmail.com
              </a>{' '}
              {t('billingTerms.s10.li2.after')}
            </li>
          </ul>
        </section>

        {/* 11. Cancellation */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s11.title')}</h2>
          <p class={pCls}>{t('billingTerms.s11.p1')}</p>
          <ul class={ul2Cls}>
            <li>{t('billingTerms.s11.li1')}</li>
            <li>{t('billingTerms.s11.li2')}</li>
            <li>{t('billingTerms.s11.li3')}</li>
          </ul>
        </section>

        {/* 12. Tax */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s12.title')}</h2>
          <p class={pCls}>{t('billingTerms.s12.p1')}</p>
        </section>

        {/* 13. Invoices and Receipts (NEW) */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s13.title')}</h2>
          <p class={pCls}>{t('billingTerms.s13.p1')}</p>
        </section>

        {/* 14. Billing Disputes (NEW) */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s14.title')}</h2>
          <p class={pCls}>
            {t('billingTerms.s14.p1')}{' '}
            <a href="mailto:human.khoai@gmail.com" class={link}>
              human.khoai@gmail.com
            </a>{' '}
            {t('billingTerms.s14.p1.after')}
          </p>
        </section>

        {/* 15. Price Changes */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s15.title')}</h2>
          <p class={pCls}>{t('billingTerms.s15.p1')}</p>
        </section>

        {/* 16. Contact */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('billingTerms.s16.title')}</h2>
          <p class={pCls}>{t('billingTerms.s16.p1')}</p>
          <p class="mt-2 text-sm text-text-secondary">
            <a href="mailto:human.khoai@gmail.com" class={link}>
              human.khoai@gmail.com
            </a>
          </p>
        </section>
      </article>
    </PublicLayout>
  );
}
